import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../ui-components/table/table";
import {
  clearPickupLog,
  getPickupLog,
  pickupDetailsSelector
} from "../../../slices/pickup/pickupDetailsSlice";
import { HUMAN_FRIENDLY_ACTIONS } from "../../pickup/common";
import { PickupLogItem } from "../../../app/data/pickup/models";

import "./pickupDetails.scss";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import { formatAdditionalInfo, extractRemainingTextAfterReason } from "../../pickup/utils/pickupLogUtils";

export interface PickupLogProps {
  pickupId: string;
  onRescheduled: (arg: { pickupNumber: string, pickupDate?: string, actionPerformed: string } | null) => void;
};

const columnHelper = createColumnHelper<PickupLogItem>();

const PickupDetailsLog: React.FC<PickupLogProps> = ({ onRescheduled, ...props }) => {
  const dispatch = useDispatch();
  const pickupDetailsState = useSelector(pickupDetailsSelector);

  const columns = [
    columnHelper.accessor("actionExecutedTime", {
      id: "actionExecutedTime",
      header: "Event Time",
      cell: (cellProps) => (
        <>
          {cellProps.getValue() && (
            <div className="xgs-pickup-details__log__date">
              <div>{cellProps.getValue().toUiDateFormat()}</div> <div className="xgs-pickup-details__log__gray-value">{cellProps.getValue().toUiTimeShortFormat()}</div>
            </div>
          )}
        </>
      ),
      size: 145,
    }),
    columnHelper.accessor("userExecuted", {
      id: "userExecuted",
      header: "User",
      size: 208,
    }),
    columnHelper.accessor("actionPerformed", {
      id: "actionPerformed",
      header: "Action",
      cell: (cellProps: any) => {
        const value = cellProps.getValue();
        return HUMAN_FRIENDLY_ACTIONS[value as keyof typeof HUMAN_FRIENDLY_ACTIONS] || value;
      },
      size: 155,
    }),
    columnHelper.display({
      id: "additionalInfo",
      size: 170,
      header: "Additional Info",
      cell: ({ row }) => {
        const data = row.original;

        const isCompletedByTerminal =
          data?.actionSource === "PORTAL" && data?.actionPerformed === "COMPLETED";

        const isCompletedByDriver =
          data?.actionSource === "DRIVER" && data?.actionPerformed === "COMPLETED";

        const { driverId, driverName } = data || {};

        const driverInfo = driverName ? (
          <span className="xgs-pickup-details__log__additional__item">
            {driverName}{" "}
            {driverId && (
              <span className="xgs-pickup-details__log__gray-value">
                ({driverId})
              </span>
            )}
          </span>
        ) : driverId ? (
          <span className="xgs-pickup-details__log__additional__item">
            {driverId}
          </span>
        ) : null;

        if (data?.actionPerformed === "ASSIGNED") {
          return (<>
            {driverInfo}
          </>);
        }

        if (isCompletedByTerminal) {
          return (
            <span className="xgs-pickup-details__log__additional__item">
              <Tag mods={{ color: TagColor.RED }}>Completed at Terminal</Tag>
            </span>
          );
        }

        if (isCompletedByDriver) {
          return (
            <>
              {driverInfo}
            </>
          );
        }

        // non-terminal, non-assigned, non-driver-completed ---
        const { reason, formattedText } = formatAdditionalInfo(data);

        if (!reason && !formattedText) return null;

        if (!reason) {
          return (
            <>
              <span
                className="xgs-pickup-details__log__additional__item"
                style={{ fontWeight: "normal" }}
              >
                {formattedText}
              </span>
              {driverInfo}
            </>
          );
        }

        const remainingText = extractRemainingTextAfterReason(formattedText, reason);

        return (
          <>
            <span className="xgs-pickup-details__log__additional__item">
              <strong>{reason}</strong>
            </span>
            {remainingText && (
              <>
                {", "}
                <span
                  className="xgs-pickup-details__log__additional__item"
                  style={{ fontWeight: "normal" }}
                >
                  {remainingText}
                </span>
              </>
            )}
            {driverInfo}
          </>
        );
      },
    })
  ];

  useEffect(() => {
    if (!props.pickupId) return;
    dispatch(getPickupLog(props.pickupId));
  }, [props.pickupId, dispatch]);

  useEffect(() => {
    if (!pickupDetailsState.pickupLog) return;
    let rescheduledToEntry = pickupDetailsState.pickupLog.find(entry => entry.actionPerformed === "RESCHEDULED_TO");
    let rescheduledEntry = pickupDetailsState.pickupLog.find(entry => entry.actionPerformed === "RESCHEDULE_PICKUP");
    const entry = rescheduledEntry || rescheduledToEntry || null;
    onRescheduled(entry ? { pickupNumber: entry.pickupNumber, pickupDate: entry.pickupDate, actionPerformed: entry.actionPerformed } : null);
  }, [pickupDetailsState.pickupLog, onRescheduled]);

  useEffect(() => {
    return () => { dispatch(clearPickupLog()) };
  }, [dispatch]);

  return (
    <div className="xgs-pickup-details__log">
      <Table
        isLoading={pickupDetailsState.requestStarted && (pickupDetailsState.requestCreator === "GET_PICKUP_LOG")}
        columns={columns}
        data={pickupDetailsState.pickupLog}
        rowHeight={48}
        minTableHeight={220}
        noResultsText="No entries"
      />
    </div>
  );
};

export default PickupDetailsLog;
